// fetch-обёртка admin API: X-Request-Id на каждый запрос, access-токен из памяти (не localStorage),
// single-flight refresh и ровно один повтор на 401 (не для /auth/*) — 07-admin.md §1/§2.
import type { z } from "zod";
import {
  apiErrorSchema,
  authTokenResponseSchema,
  splitErrorDetails,
  type Issue,
  type RequestFieldError,
} from "./schemas";
import { useAuthStore } from "@/shared/auth/store";

const API_PREFIX = "/admin/api";

// Только сами шаги входа/выхода не должны запускать silent-refresh на 401 — иначе логин бы рекурсивно
// пытался обновиться по несуществующему токену. /auth/sessions и /auth/sessions/{id} — обычные защищённые
// ручки (viewer, Bearer-токен) и ДОЛЖНЫ получать обычный refresh+retry, поэтому префиксной проверки
// `path.startsWith("/auth/")` недостаточно (см. pre-merge review SHY-61, дефект 9).
const AUTH_FLOW_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout"];

function isAuthFlowPath(path: string): boolean {
  return AUTH_FLOW_PATHS.some((p) => path === p || path.startsWith(`${p}?`));
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string,
    /** Issue документа у 422 `validation_error` — контракт, раздел «Общее». */
    public readonly details: Issue[] = [],
    /** Ошибки полей запроса от валидатора FastAPI (тот же 422, другой формат). */
    public readonly fieldErrors: RequestFieldError[] = [],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Текст ошибки для тоста: сообщение сервера плюс поля, которые он не принял. Обычные `Error`
 * (например «правки не сохранены» из DraftProvider) отдаются как есть — их текст уже осмысленный.
 */
export function apiErrorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.fieldErrors.length === 0) return error.message;
    const fields = error.fieldErrors
      .map(
        (item) =>
          `${item.loc.filter((part) => part !== "body").join(".") || "запрос"} (${item.type})`,
      )
      .join(", ");
    return `${error.message}: ${fields}`;
  }
  return error instanceof Error ? error.message : fallback;
}

/** Бэкенд не отвечает: 502/503/504 от ingress (или прокси Vite) и обрыв сети. */
export const UNAVAILABLE_MESSAGE =
  "Бэкенд недоступен — админка не может ни прочитать, ни сохранить данные. Проверьте, что сервис поднят.";

function isUnavailableStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

export function isUnavailable(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || isUnavailableStatus(error.status));
}

let refreshPromise: Promise<boolean> | null = null;

/** Единственный одновременный refresh на всё приложение — параллельные 401 ждут один и тот же вызов. */
async function refreshSession(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const res = await fetch(`${API_PREFIX}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      // 401 — сессии нет; 502/503/504 — бэкенд молчит. Это разные экраны для пользователя.
      useAuthStore.getState().setUnavailable(isUnavailableStatus(res.status));
      if (!res.ok) return false;
      const parsed = authTokenResponseSchema.safeParse(await res.json());
      if (!parsed.success) return false;
      useAuthStore.getState().setSession(parsed.data);
      return true;
    } catch {
      useAuthStore.getState().setUnavailable(true); // сеть оборвалась / бэкенд не поднят
      return false;
    }
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

function buildHeaders(init: RequestInit): Headers {
  const headers = new Headers(init.headers);
  headers.set("X-Request-Id", crypto.randomUUID());
  const token = useAuthStore.getState().accessToken;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return headers;
}

/** Низкоуровневый fetch с авторизацией и ровно одним повтором на 401 (кроме /auth/*). */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const isAuthEndpoint = isAuthFlowPath(path);
  const doFetch = async () => {
    try {
      const response = await fetch(`${API_PREFIX}${path}`, {
        ...init,
        headers: buildHeaders(init),
        credentials: "include",
      });
      // Сервер ответил — значит он жив, каким бы ни был статус. Иначе плашка «бэкенд недоступен»
      // висела бы вечно: её снимал только успешный вход (pre-merge review SHY-68, дефект 4).
      useAuthStore.getState().setUnavailable(isUnavailableStatus(response.status));
      return response;
    } catch {
      // fetch бросает только на сетевом уровне (бэкенд не поднят, оборвалась сеть) — превращаем в
      // ApiError со status 0, иначе react-query покажет техническое "Failed to fetch".
      throw new ApiError(0, "backend_unavailable", UNAVAILABLE_MESSAGE, "", []);
    }
  };

  let res = await doFetch();
  if (res.status === 401 && !isAuthEndpoint) {
    const refreshed = await refreshSession();
    if (!refreshed) {
      useAuthStore.getState().clear();
      return res;
    }
    res = await doFetch();
  }
  return res;
}

async function throwIfError(res: Response): Promise<void> {
  if (res.ok) return;
  const body: unknown = await res.json().catch(() => null);
  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) {
    const { issues, fieldErrors } = splitErrorDetails(parsed.data.error.details);
    throw new ApiError(
      res.status,
      parsed.data.error.code,
      parsed.data.error.message,
      parsed.data.error.request_id,
      issues,
      fieldErrors,
    );
  }
  // 502/503/504 приходят от ingress/прокси без конверта — у них нет ни кода, ни request_id.
  if (isUnavailableStatus(res.status)) {
    throw new ApiError(res.status, "backend_unavailable", UNAVAILABLE_MESSAGE, "");
  }
  throw new ApiError(
    res.status,
    "unknown",
    `HTTP ${res.status}`,
    res.headers.get("X-Request-Id") ?? "",
  );
}

/** JSON-запрос с runtime-валидацией ответа по zod-схеме. */
export async function apiRequest<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  init: RequestInit = {},
): Promise<z.infer<S>> {
  const res = await apiFetch(path, init);
  await throwIfError(res);
  if (res.status === 204) return undefined as z.infer<S>;
  return schema.parse(await res.json());
}

/** Мутация без содержательного тела ответа (204). */
export async function apiRequestVoid(path: string, init: RequestInit = {}): Promise<void> {
  const res = await apiFetch(path, init);
  await throwIfError(res);
}

/** Ответ text/csv (аналитика) — без zod, отдаём как текст для скачивания/предпросмотра. */
export async function apiRequestCsv(path: string): Promise<string> {
  const res = await apiFetch(path);
  await throwIfError(res);
  return res.text();
}

export function apiGet<S extends z.ZodTypeAny>(path: string, schema: S): Promise<z.infer<S>> {
  return apiRequest(path, schema);
}

export function apiPost<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  body?: unknown,
): Promise<z.infer<S>> {
  return apiRequest(path, schema, {
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPut<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  body: unknown,
): Promise<z.infer<S>> {
  return apiRequest(path, schema, { method: "PUT", body: JSON.stringify(body) });
}

export function apiPatch<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  body: unknown,
): Promise<z.infer<S>> {
  return apiRequest(path, schema, { method: "PATCH", body: JSON.stringify(body) });
}

export function apiDelete(path: string): Promise<void> {
  return apiRequestVoid(path, { method: "DELETE" });
}

/** Silent refresh при загрузке страницы, чтобы F5 не выкидывал на логин (07-admin.md §2). */
export async function bootstrapAuth(): Promise<void> {
  useAuthStore.getState().setStatus("loading");
  const ok = await refreshSession();
  useAuthStore.getState().setStatus(ok ? "authenticated" : "unauthenticated");
}

/** Только для тестов client.ts — сбросить внутреннее состояние single-flight между кейсами. */
export function __resetClientForTests(): void {
  refreshPromise = null;
}
